import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SiteSummaryContext } from '../../../../shared/contexts/site-summary.context';
import { ClustersResource } from '../services/clusters.resource';
import { Subscription, switchMap } from 'rxjs';
import { Cluster, SiteSummary } from '../../../../shared/models/generated/ziroModels';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { ZiroModalPromptModalOptions } from '../../../../modals/prompt-modal/prompt-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';
import { WebexCallingDataContext } from '../../../resources/webex-calling-data-context';

interface ClusterListRow extends DatatableRow {
  name: string;
  servers: string[];
}

@Component({
  selector: 'ziro-clusters-list',
  templateUrl: './clusters-list.component.html',
  standalone: false,
})
export class ClustersListComponent implements OnInit, OnDestroy {
  private siteSummaryContext = inject(SiteSummaryContext);
  private clustersResource = inject(ClustersResource);
  private breadcrumbsService = inject(BreadcrumbsService);
  private bottomNavService = inject(BottomNavService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private ziroModalService = inject(ZiroModalService);
  /**
   * Required to initialize Webex Calling data for large environments.
   * Keep this injection in place to avoid delaying data on large-environment loads.
   */
  private _webexCallingDataContext = inject(WebexCallingDataContext);

  isLoading = true;
  tableDataAutomation = 'clusters-list-table';
  columns: DatatableColumn<ClusterListRow>[] = [
    {
      name: 'name',
      label: 'tkey;admin.site_management.cluster_sites.cluster_name',
      columnSize: 'col-sm-5',
    },
    {
      name: 'servers',
      label: 'tkey;admin.site_management.cluster_sites.servers',
      columnSize: 'col-sm-5',
      sortFn: (a: string[], b: string[]) => a[0]?.localeCompare(b[0]),
    },
  ];
  data: ClusterListRow[] = [];

  private _subscription = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.site_management.cluster_sites.header' }]);

    this._initBottomNav();

    const siteSummarySub = this.siteSummaryContext.state$.subscribe((siteSummary) => {
      this._buildTableRows(siteSummary);
      this.isLoading = false;
    });
    this._subscription.add(siteSummarySub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private _buildTableRows(siteSummary: SiteSummary): void {
    this.data = siteSummary.clusters.map((cluster): ClusterListRow => {
      const servers: string[] = [cluster.cucmServerDescription];
      if (cluster.impServerId) {
        servers.push(cluster.impServerDescription);
      }
      if (cluster.pcceServerId) {
        servers.push(cluster.pcceServerDescription);
      }

      return {
        name: cluster.name,
        servers: servers,
        badges: {
          servers: {
            values: servers.map((server) => ({
              label: server,
              cssClass: 'bg-secondary',
            })),
          },
        },
        actions: [
          {
            dataAutomation: 'clusters-list-sites-button',
            style: ButtonStyles.OUTLINE_PRIMARY,
            type: ButtonTypes.LINK,
            icon: ZiroIcons.NEXT,
            href: window.location.href + `/${cluster.id}/sites`,
          },
          {
            dataAutomation: 'clusters-list-delete-button',
            style: ButtonStyles.OUTLINE_DANGER,
            type: ButtonTypes.BUTTON,
            icon: ZiroIcons.DELETE_SMALL,
            onClick: () => {
              this._openDeleteClusterModal(cluster);
            },
          },
          {
            dataAutomation: 'clusters-list-edit-button',
            style: ButtonStyles.OUTLINE_DEFAULT,
            type: ButtonTypes.LINK,
            icon: ZiroIcons.EDIT,
            href: window.location.href + `/${cluster.id}`,
          },
        ],
      };
    });
  }

  private _initBottomNav(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'add-cluster',
          label: 'tkey;admin.site_management.add_cluster.button',
          icon: ZiroIcons.ADD,
          type: ButtonTypes.LINK,
          href: window.location.href + '/new',
          dataAutomation: 'clusters-list-add-cluster-button',
          buttonClass: ButtonStyles.PRIMARY,
        },
      ]),
    );
  }

  private _openDeleteClusterModal(cluster: Cluster): void {
    const options: ZiroModalPromptModalOptions = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        promptBody: this.translateService.instant(
          'tkey;admin.site_management.cluster_sites.cluster_confirmation_delete.text',
          {
            clusterName: cluster.name,
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'prompt-modal-cancel',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'prompt-modal-confirm',
            cb: () =>
              this.clustersResource.delete(cluster.id).pipe(
                switchMap(() => this.siteSummaryContext.refreshSiteSummary()),
                tap(() => {
                  this.toastService.push(
                    ToastTypes.INFO,
                    ZiroIcons.DELETE_SMALL,
                    'tkey;admin.clusters.delete.toast.title',
                    'tkey;admin.clusters.delete.toast.message',
                    { clusterName: cluster.name },
                    null,
                    'text-danger',
                  );
                }),
              ),
          },
        ],
      },
    };

    this.ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }
}
